import React, { useEffect, useMemo, useRef, useState } from 'react';

import alert from '../../helpers/alert';
import extractErrorMessage from '../../utils/extractErrorMessage';
import styles from './sheetSync.module.css';
import { getPriceList } from '../../api/prices';

const emptyTarget = (markId = '', year = '') => ({
  markId: markId ? String(markId) : '',
  modelId: '',
  year,
  complectationId: '',
  volume: '',
  wdId: '',
});

// Привязка с бэка → поля формы. wdId 0 — «у машины не указан», это отдельное значение, не «любой»
export const toForm = (t) => ({
  markId: String(t.markId ?? ''),
  modelId: t.modelId != null ? String(t.modelId) : '',
  year: t.issueYear || '',
  complectationId: t.complectationId != null ? String(t.complectationId) : '',
  volume: t.engineVolume || '',
  wdId: t.wdId != null ? String(t.wdId) : '',
});

// Поля формы → тело привязки для бэка
export const toLink = (sheetKey, f) => ({
  sheetKey,
  modelId: Number(f.modelId),
  issueYear: f.year.trim() || null,
  complectationId: f.complectationId ? Number(f.complectationId) : null,
  engineVolume: f.volume.trim() || null,
  wdId: f.wdId === '' ? null : Number(f.wdId),
});

// Редактор привязки строки: выбирается правило приказа (модель, год, комплектация, объём, привод).
// Правил может быть несколько — в каталоге бывают двойники комплектации, и цена из таблицы нужна обоим.
const LinkEditor = ({ sheetKey, problem, marks, initial, suggestedMarkId, busy, saveText, onSave, onCancel }) => {
  const guess = useMemo(() => {
    if (suggestedMarkId) return suggestedMarkId;
    const brand = (problem?.brand || '').toLowerCase();
    return marks.find((m) => brand.includes((m.mark || '').toLowerCase()))?.markId || '';
  }, [problem, marks, suggestedMarkId]);

  const [targets, setTargets] = useState(() =>
    initial?.length ? initial.map(toForm) : [emptyTarget(guess, problem?.year || '')]
  );
  const [dicts, setDicts] = useState({});
  const requested = useRef(new Set());

  const markKey = targets.map((t) => t.markId).join(',');
  useEffect(() => {
    [...new Set(targets.map((t) => t.markId).filter(Boolean))].forEach((id) => {
      if (requested.current.has(id)) return;
      requested.current.add(id);
      getPriceList(id)
        .then((res) => setDicts((prev) => ({ ...prev, [id]: res.data })))
        .catch((err) => alert.error(extractErrorMessage(err) || 'Не удалось загрузить справочники марки'));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [markKey]);

  const set = (i, patch) => setTargets((prev) => prev.map((t, k) => (k === i ? { ...t, ...patch } : t)));
  const suffix = (i) => (targets.length > 1 ? ` ${i + 1}` : '');
  const ready = targets.every((t) => t.modelId);

  return (
    <div className={styles.editor}>
      <div className={styles.editorHelp}>
        Укажите правило приказа, к которому относится строка {sheetKey}. Объём и привод оставьте пустыми, если правило
        действует на любой объём и привод. Привязка сохраняется сразу, по кнопке ниже, цены она не меняет.
      </div>
      {targets.map((t, i) => {
        const dict = dicts[t.markId];
        const trims = (dict?.complectations || []).filter((c) => String(c.modelId) === t.modelId);
        return (
          <div className={styles.editorRow} key={i}>
            <label>
              Марка
              <select
                aria-label={`Марка привязки${suffix(i)}`}
                value={t.markId}
                onChange={(e) => set(i, { markId: e.target.value, modelId: '', complectationId: '', wdId: '' })}
              >
                <option value="">—</option>
                {marks.map((m) => (
                  <option key={m.markId} value={m.markId}>
                    {m.mark}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Модель
              <select
                aria-label={`Модель привязки${suffix(i)}`}
                value={t.modelId}
                onChange={(e) => set(i, { modelId: e.target.value, complectationId: '' })}
              >
                <option value="">—</option>
                {(dict?.models || []).map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.name}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Год (пусто — любой)
              <input
                aria-label={`Год привязки${suffix(i)}`}
                value={t.year}
                onChange={(e) => set(i, { year: e.target.value })}
              />
            </label>
            <label>
              Комплектация
              <select
                aria-label={`Комплектация привязки${suffix(i)}`}
                value={t.complectationId}
                onChange={(e) => set(i, { complectationId: e.target.value })}
              >
                <option value="">вся модель</option>
                {trims.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.title}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Объём (пусто — любой)
              <input
                aria-label={`Объём привязки${suffix(i)}`}
                value={t.volume}
                onChange={(e) => set(i, { volume: e.target.value })}
              />
            </label>
            <label>
              Привод
              <select
                aria-label={`Привод привязки${suffix(i)}`}
                value={t.wdId}
                onChange={(e) => set(i, { wdId: e.target.value })}
              >
                <option value="">любой</option>
                <option value="0">не указан у машины</option>
                {(dict?.wd || []).map((w) => (
                  <option key={w.id} value={w.id}>
                    {w.title}
                  </option>
                ))}
              </select>
            </label>
            {targets.length > 1 && (
              <button
                type="button"
                className={styles.mini}
                title="Убрать это правило из привязки"
                onClick={() => setTargets((prev) => prev.filter((_, k) => k !== i))}
              >
                Убрать
              </button>
            )}
          </div>
        );
      })}
      <div className={styles.editorActions}>
        <button
          type="button"
          className={styles.mini}
          title="Добавить ещё одно правило: так делают, когда в каталоге два одинаковых названия комплектации"
          onClick={() => setTargets((prev) => [...prev, emptyTarget(prev[prev.length - 1]?.markId, prev[prev.length - 1]?.year || '')])}
        >
          + ещё правило
        </button>
        <span className={styles.grow} />
        <button
          type="button"
          className={styles.miniPrimary}
          disabled={busy || !ready}
          title={ready ? 'Сохранить привязку сейчас' : 'Выберите модель в каждом правиле'}
          onClick={() => onSave(targets.map((t) => toLink(sheetKey, t)))}
        >
          {saveText || 'Сохранить привязку'}
        </button>
        <button type="button" className={styles.mini} onClick={onCancel}>
          Отмена
        </button>
      </div>
    </div>
  );
};

export default LinkEditor;
